/**
 * API Error Utilities
 *
 * Helper functions for handling API error responses.
 * Types and schemas are defined in @contracts (flights-api-error.ts).
 */
import type { ApiErrorResponse, PopupErrorDetails } from '@contracts';

// ============================================
// Helper Functions
// ============================================

/**
 * Extract popup-friendly error details from API error response.
 * Returns null if response is not a failure or doesn't have error message.
 *
 * @param data - API response that may contain error details
 * @returns PopupErrorDetails if error found, null otherwise
 */
export function extractPopupErrorDetails(data: ApiErrorResponse): PopupErrorDetails | null {
  if (data.responseStatus !== 'FAILED' || !data.errorDetails?.errorMessage) {
    return null;
  }
  const desc = data.errorDetails.errorDescription;
  return {
    errorMessage: data.errorDetails.errorMessage,
    ...(desc !== undefined && desc !== '' ? { errorDescription: desc } : {}),
  };
}

/**
 * Check if response is an API error response
 *
 * @param data - Unknown data to check
 * @returns True if data is an API error response with FAILED status
 */
export function isApiErrorResponse(data: unknown): data is ApiErrorResponse {
  if (data === null || typeof data !== 'object') {
    return false;
  }
  const response = data as ApiErrorResponse;
  return response.responseStatus === 'FAILED' && response.errorDetails !== undefined;
}
